import type { TElement, TText } from '@udecode/plate-common';
import { focusEditor, isMarkActive } from '@udecode/plate-common';
import type { PlateEditor } from '@udecode/plate-core';
import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import type { FC } from 'react';
import type { Props as ReactSelectProps } from 'react-select';
import { components } from 'react-select';
import type { RevenueClient } from 'types/clients.types';
import type { Invoice } from 'types/invoice.types';
import { InvoiceTypeEnum } from 'types/invoice.types';
import { XIconOutline } from 'utils/icons';
import type { TextEditorType } from '../TextEditor';
import {
  ELEMENT_PARAGRAPH,
  MARK_BOLD,
  MARK_ITALIC,
  MARK_UNDERLINE,
} from '../TextEditor/TextEditorPlugins';
import { BoldSignIcon, ItalicSignIcon, UnderlineSignIcon } from './CustomIcons';

export type FileState = {
  key: string;
  file?: File;
  path?: string;
  type?: string;
  name: string;
  progress?: number;
  isLoading?: boolean;
  isError?: boolean;
  canRetry?: boolean;
  cancel?: () => void;
};

export const convertLineBreaksToP = (str: string | undefined) =>
  str
    ?.split(/\n+/g)
    .map((s) => `<p>${s}</p>`)
    .join('');

export const convertTextNodesToElements = (nodes: (TElement | TText)[]) => {
  // fix the differences between web and mobile templates
  const hasNoPs = nodes.every((node) => {
    return node.type !== ELEMENT_PARAGRAPH;
  });

  if (hasNoPs) {
    return [
      {
        type: ELEMENT_PARAGRAPH,
        children: [{ text: '' }, ...nodes, { text: '' }],
      },
    ] as TElement[];
  }

  return nodes.map((node) => {
    if (node.type === ELEMENT_PARAGRAPH) {
      return {
        ...node,
        type: ELEMENT_PARAGRAPH,
        children: [
          { text: '' },
          ...(node.children as (TElement | TText)[]),
          { text: '' },
        ],
      };
    }
    return {
      type: ELEMENT_PARAGRAPH,
      children: [{ text: '' }, node, { text: '' }],
    };
  }) as TElement[];
};

export const convertLineBreaksToFragment = (str: string | undefined) =>
  str
    ?.split(/\n+/g)
    .map((text) => ({ type: ELEMENT_PARAGRAPH, children: [{ text }] })) || [];

export const toggleMark = (editor: PlateEditor, key: string, value = true) => {
  const isActive = isMarkActive(editor, key);
  if (isActive) editor.removeMark(key);
  else editor.addMark(key, value);
  focusEditor(editor);
};

const getButtonsClasses = (isActive: boolean) =>
  classNames(
    'rounded',
    isActive ? 'ring-1 ring-taxes-700 bg-taxes-50' : 'bg-transparent',
  );

type AttachmentsObjectType = Partial<{
  attachInvoicePdf: boolean;
  attachInvoiceUbl21: boolean;
  attachInvoiceUblBe: boolean;
  attachInvoiceUbl31: boolean;
  attachInvoiceZugferd: boolean;
  attachQuotePdf: boolean;
}>;

export enum RevenueAttachmentsEnum {
  attachInvoicePdf = 'attachInvoicePdf',
  attachInvoiceUbl21 = 'attachInvoiceUbl21',
  attachInvoiceUblBe = 'attachInvoiceUblBe',
  attachInvoiceUbl31 = 'attachInvoiceUbl31',
  attachQuotePdf = 'attachQuotePdf',
}

export const adaptAttachmentsArray = (attachments: string[]) => {
  return attachments.reduce(
    (acc, att) => ({ ...acc, [att]: true }),
    {} as AttachmentsObjectType,
  );
};

export const tagsInputStyleProps: ReactSelectProps['components'] = {
  IndicatorSeparator: () => null,
  DropdownIndicator: () => null,
  MultiValueRemove: (props) => {
    return (
      <components.MultiValueRemove {...props}>
        <XIconOutline className="w-4 h-4 text-red-400" />
      </components.MultiValueRemove>
    );
  },
};

export const CommonEditorActions: FC<{
  editor: TextEditorType;
  events?: Record<string, () => void>;
}> = ({ editor, events }) => {
  return (
    <>
      <Button
        asDefault
        className={getButtonsClasses(isMarkActive(editor, MARK_BOLD))}
        onClick={() => {
          events?.onToggleBold?.();
          toggleMark(editor, MARK_BOLD);
        }}
      >
        <BoldSignIcon className="text-primary-700 w-5 h-5" />
      </Button>
      <Button
        asDefault
        className={getButtonsClasses(isMarkActive(editor, MARK_ITALIC))}
        onClick={() => {
          events?.onToggleItalic?.();
          toggleMark(editor, MARK_ITALIC);
        }}
      >
        <ItalicSignIcon className="text-primary-700 w-5 h-5" />
      </Button>
      <Button
        asDefault
        className={getButtonsClasses(isMarkActive(editor, MARK_UNDERLINE))}
        onClick={() => {
          events?.onToggleUnderline?.();
          toggleMark(editor, MARK_UNDERLINE);
        }}
      >
        <UnderlineSignIcon className="text-primary-700 w-5 h-5" />
      </Button>
    </>
  );
};

export const getRevenueEmailContextFromSavedRevenue = (
  revenue?: Invoice<RevenueClient>,
) => {
  return {
    isCreditNote: revenue?.type === InvoiceTypeEnum.CREDIT_NOTE,
    defaultTemplateLang: revenue?.userSnapshot?.settings?.invoices?.language,
    parentQuoteId: revenue?.quoteId,
    revenueId: revenue?._id,
    revenuePDFPath: revenue?.filePath,
    revenueNumber: revenue?.revenueNumber,
    revenueDueDate: revenue?.dueDate,
    clientEmail: revenue?.client?.email,
    clientName: revenue?.client?.name,
    clientType: revenue?.client?.type,
    revenueTotalAmountInclVAT:
      (revenue?.totalAmountInclVAT || 0) - (revenue?.downPayment || 0),
    revenueCurrency: revenue?.currency,
  };
};

export const getRevenueEmailContextFromRevenueForm = (revenue?: any) => {
  return {
    isCreditNote: revenue?.type === InvoiceTypeEnum.CREDIT_NOTE,
    defaultTemplateLang: revenue?.settings?.language,
  };
};

export const externalAttachmentsObjectToArray = (
  data: Record<string, FileState>,
) => {
  return Object.values(data)
    .filter((fileState) => fileState?.path)
    .map((fileState) => ({
      filePath: fileState.path as string,
      name: fileState.name,
    }));
};
